import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Card, CardContent } from '../Card';
import { renderIcon } from '../Dashboard/iconUtils';
import { cn } from '../../../lib/utils';

import { useTranslation } from 'react-i18next';

export interface StatusCardProps {
  /** The title of the status card */
  title?: string;
  /** The status message */
  message?: string;
  /** The status type */
  status?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  /** Optional subtitle */
  subtitle?: string;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Optional custom className for styling */
  className?: string;
  /** Optional icon to display (Lucide icon name as string) */
  icon?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Whether to show timestamp */
  showTimestamp?: boolean;
  /** Timestamp value */
  timestamp?: string;
  /** Optional action button text */
  actionText?: string;
  /** Optional action button handler */
  onAction?: () => void;
  /** Whether data is streaming from LLM */
  isStreaming?: boolean;
}

export const StatusCard: React.FC<StatusCardProps> = ({
  title,
  message,
  status = 'neutral',
  subtitle,
  isLoading = false,
  className,
  icon,
  onClick,
  showTimestamp = false,
  timestamp,
  actionText,
  onAction,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  // Show loading state if explicitly loading or if no data is provided
  if (isLoading || (!title && !message && !subtitle)) {
    return (
      <Card className={cn('h-full', className)} onClick={onClick}>
        <CardContent className="flex h-full items-center p-6">
          <div className="w-full animate-pulse">
            <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="h-6 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: 'check-circle',
          color: '#10b981',
          borderClass: 'border-l-green-400',
          titleClass: 'text-green-900',
        };
      case 'warning':
        return {
          icon: 'alert-triangle',
          color: '#f59e0b',
          borderClass: 'border-l-yellow-400',
          titleClass: 'text-yellow-900',
        };
      case 'error':
        return {
          icon: 'x-circle',
          color: '#ef4444',
          borderClass: 'border-l-red-400',
          titleClass: 'text-red-900',
        };
      case 'info':
        return {
          icon: 'info',
          color: '#3b82f6',
          borderClass: 'border-l-blue-400',
          titleClass: 'text-blue-900',
        };
      case 'neutral':
      default:
        return {
          icon: 'circle',
          color: '#6b7280',
          borderClass: 'border-l-gray-400',
          titleClass: 'text-gray-900 dark:text-gray-100',
        };
    }
  };

  const renderStatusIcon = () => {
    const config = getStatusConfig();
    const iconName = icon || config.icon;
    const color = config.color;
    const size = 20; // Bigger icon like CategoryCard

    const iconElement = renderIcon({ name: iconName, size, color });

    if (!iconElement) {
      return null;
    }

    return (
      <div
        className="flex items-center justify-center rounded"
        style={{
          backgroundColor: `${color}15`,
          width: `${size + 16}px`,
          height: `${size + 16}px`,
        }}
      >
        {iconElement}
      </div>
    );
  };

  const renderLoadingContent = () => (
    <div className="animate-pulse">
      <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
      <div className="mb-2 h-6 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
      <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
    </div>
  );

  const config = getStatusConfig();

  return (
    <Card
      className={twMerge('border-l-4', config.borderClass, className)}
      onClick={onClick}
      isLoading={isLoading}
      loadingContent={renderLoadingContent()}
    >
      <div
        className={cn(
          'flex flex-col',
          className?.includes('h-full') && 'h-full',
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="mb-2 flex items-center gap-3">
              {renderStatusIcon()}
              <div
                className={twMerge(
                  'text-base font-semibold',
                  config.titleClass,
                )}
              >
                {title || t('common:status')}
              </div>
            </div>
          </div>
          {showTimestamp && timestamp && (
            <div className="flex-shrink-0">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {timestamp}
              </span>
            </div>
          )}
        </div>

        <CardContent
          className={cn(
            className?.includes('h-full') &&
              'flex flex-1 flex-col justify-center',
          )}
        >
          <div className="mb-2">
            <div className="text-sm leading-relaxed font-normal text-gray-900 dark:text-gray-100">
              {message || t('noMessageProvided')}
            </div>
            {subtitle && (
              <div className="mt-1 text-xs leading-relaxed font-normal text-gray-500 dark:text-gray-400">
                {subtitle}
              </div>
            )}
          </div>

          {actionText && onAction && (
            <div className="mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAction();
                }}
                className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium tracking-wide text-white transition-colors hover:bg-gray-800"
              >
                {actionText}
              </button>
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
};
